import styled from '@emotion/styled';
import { css } from '@emotion/react';

import theme from 'styles/theme';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';
export type ButtonShape = 'default' | 'pill';

type StyledProps = {
  $variant: ButtonVariant;
  $size: ButtonSize;
  $shape: ButtonShape;
  $fullWidth?: boolean;
  $color?: string;
  $backgroundColor?: string;
};

const sizeStyles: Record<ButtonSize, ReturnType<typeof css>> = {
  sm: css`
    height: 25px;
    padding: 0 12px;
    font-size: 12px;
    border-radius: 4px;
  `,
  md: css`
    height: 37px;
    padding: 0 16px;
    font-size: 14px;
    border-radius: 6px;
  `,
  lg: css`
    height: 44px;
    padding: 0 20px;
    font-size: 16px;
    border-radius: 6px;
  `,
  // xl: spec exato do Figma (botão "Selecionar todos" = 163x48, texto 16px,
  // padding 12px vertical / 16px horizontal, peso 700)
  xl: css`
    height: 48px;
    padding: 12px 16px;
    font-size: 16px;
    font-weight: 700;
    border-radius: 8px;
  `,
};

const getVariantStyles = (
  variant: ButtonVariant,
  color?: string,
  backgroundColor?: string
): ReturnType<typeof css> => {
  switch (variant) {
    case 'primary':
      return css`
        background: ${backgroundColor || theme.colors.mediumPurple};
        color: ${color || '#fff'};
        border: none;

        &:hover:not(:disabled) {
          background: ${backgroundColor || '#328cfd'};
        }
      `;
    case 'secondary':
      return css`
        background: ${backgroundColor || '#fff'};
        color: ${color || theme.colors.blue};
        border: 1px solid ${color || theme.colors.blue};

        &:hover:not(:disabled) {
          opacity: 0.85;
        }
      `;
    case 'ghost':
      return css`
        background: ${backgroundColor || 'transparent'};
        color: ${color || theme.colors.blue};
        border: none;

        &:hover:not(:disabled) {
          opacity: 0.75;
        }
      `;
    default:
      return css``;
  }
};

export const StyledButton = styled('button', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-family: 'Mangueira', sans-serif;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  transition: background 0.2s ease, opacity 0.2s ease;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};

  ${({ $size }) => sizeStyles[$size]}
  ${({ $variant, $color, $backgroundColor }) =>
    getVariantStyles($variant, $color, $backgroundColor)}

  /* xl deixa a borda (quando houver) mais grossa — aplicado após o variant */
  ${({ $size }) =>
    $size === 'xl' &&
    css`
      border-width: 2px;
    `}

  /* pill afeta SOMENTE o raio — sobrescreve o radius do size */
  ${({ $shape }) =>
    $shape === 'pill' &&
    css`
      border-radius: 999px;
    `}

  /* a11y: foco visível por teclado (substitui o outline:none global) */
  &:focus-visible {
    outline: 2px solid ${theme.colors.blue};
    outline-offset: 2px;
  }

  &:active:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;
